import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';

// Data
import { GET_ASSESSMENT_SESSION_LIST, GET_ARCHIVED_ASSESSMENT_SESSION_LIST, GET_CLOSED_ASSESSMENT_SESSION_LIST, COUNT_ARCHIVED_ASSESSMENT_SESSIONS, COUNT_CLOSED_ASSESSMENT_SESSIONS } from '../../../lib/queries/assessment-session.queries';
import { getAuthUser } from '../../../lib/services/auth.service';
import { buildCloseAssessmentSession, buildOpenAssessmentSession, buildRemoveAssessmentSession, buildRestoreAssessmentSession, useAssessmentSessionListScope } from '../../../lib/services/assessment-session.service';

// Components
import { AssessmentSessionListContainer } from './assessment-session-list.container';

export const AssessmentSessionListData = () => {
  const [viewingArchived, setViewingArchived] = useState(false);
  const [viewingClosed, setViewingClosed] = useState(false);

  const user = getAuthUser();
  const { variables, loading: ageGroupIdsLoading, error: ageGroupIdsError } = useAssessmentSessionListScope();
  const ageGroupIds = variables.ageGroupIds;

  const { data: countArchivedData, loading: countArchivedLoading, error: countArchivedError } = useQuery(COUNT_ARCHIVED_ASSESSMENT_SESSIONS, {
    skip: !user || !ageGroupIds,
    variables,
    fetchPolicy: 'cache-and-network'
  });

  const { data: countClosedData, loading: countClosedLoading, error: countClosedError } = useQuery(COUNT_CLOSED_ASSESSMENT_SESSIONS, {
    skip: !user || !ageGroupIds,
    variables,
    fetchPolicy: 'cache-and-network'
  });

  const { data: sessionData, loading: sessionsLoading, error: sessionsError } = useQuery(GET_ASSESSMENT_SESSION_LIST, {
    skip: !user || !ageGroupIds,
    variables,
    fetchPolicy: 'cache-and-network'
  });

  const { data: archivedData, loading: archivedLoading, error: archivedError } = useQuery(GET_ARCHIVED_ASSESSMENT_SESSION_LIST, {
    skip: !user || !ageGroupIds || !viewingArchived,
    variables,
    fetchPolicy: 'cache-and-network'
  });

  const { data: closedData, loading: closedLoading, error: closedError } = useQuery(GET_CLOSED_ASSESSMENT_SESSION_LIST, {
    skip: !user || !ageGroupIds || !viewingClosed,
    variables,
    fetchPolicy: 'cache-and-network'
  });

  const assessmentSessions: IAssessmentSession[] = useMemo(() => {
    if (viewingArchived) {
      return (archivedData && archivedData.archivedAssessmentSessions) || [];
    } else if (viewingClosed) {
      return (closedData && closedData.closedAssessmentSessions) || [];
    }

    return (sessionData && sessionData.assessmentSessions) || [];
  }, [viewingArchived, archivedData, viewingClosed, closedData, sessionData]);

  const countArchivedAssessmentSessions = useMemo(() =>
    (countArchivedData && _.isNumber(countArchivedData.countArchivedAssessmentSessions)) ? countArchivedData.countArchivedAssessmentSessions as number : 0,
    [countArchivedData]
  );

  const countClosedAssessmentSessions = useMemo(() =>
    (countClosedData && _.isNumber(countClosedData.countClosedAssessmentSessions)) ? countClosedData.countClosedAssessmentSessions as number : 0,
    [countClosedData]
  );

  const { removeAssessmentSession } = buildRemoveAssessmentSession();
  const { restoreAssessmentSession } = buildRestoreAssessmentSession();
  const { closeAssessmentSession } = buildCloseAssessmentSession();
  const { openAssessmentSession } = buildOpenAssessmentSession();

  const loading = useMemo(() =>
    !assessmentSessions.length && (ageGroupIdsLoading || sessionsLoading || archivedLoading || closedLoading || countArchivedLoading || countClosedLoading),
    [assessmentSessions, ageGroupIdsLoading, sessionsLoading, archivedLoading, closedLoading, countArchivedLoading, countClosedLoading]
  );

  return <AssessmentSessionListContainer
    loading={loading}
    assessmentSessions={assessmentSessions}
    countArchivedSessions={countArchivedAssessmentSessions}
    countClosedSessions={countClosedAssessmentSessions}
    fetchError={Boolean(ageGroupIdsError) || Boolean(sessionsError) || Boolean(archivedError) || Boolean(closedError) || Boolean(countArchivedError) || Boolean(countClosedError)}
    viewingArchived={viewingArchived}
    setViewingArchived={setViewingArchived}
    viewingClosed={viewingClosed}
    setViewingClosed={setViewingClosed}
    restoreSession={restoreAssessmentSession}
    removeSession={removeAssessmentSession}
    closeSession={closeAssessmentSession}
    openSession={openAssessmentSession}
  />;
};
